import type { ComponentType } from "react"
import type { FeatureFlags } from "@/types"
import type { PermissionName } from "@/lib/permissions.generated"
import { hasFlag } from "@/lib/feature-flags"

/**
 * THE settings-section registry (pathfinder U10). One entry per section drives
 * everything that used to live in five hand-maintained id lists:
 *
 *   - the rail rows + their order (settings-nav renders SECTION_ORDER)
 *   - the settings page's render switch (lazy `load` thunks → next/dynamic)
 *   - the deep-link guard (sectionVisible — the SAME gate the rail uses)
 *   - the rail-reachability rules (deep-link-only sections)
 *   - the sidebar's "may this user reach /settings at all" permission set
 *
 * This module imports NO React runtime (the `load` thunks are plain dynamic
 * imports; ComponentType is a type-only import) so it stays safe to pull into
 * the app sidebar and other non-React-tree consumers.
 *
 * Declaration order IS the rail order (grouped Clinic → HR → Pipeline →
 * Messaging → Finance → Integrations → System by settings-nav) and drives
 * firstVisibleSection.
 */
export interface SettingsSectionDef {
  /** Required Spatie permission; undefined = permission-vacuous section. */
  permission?: PermissionName
  /** Any one of these permissions grants access to the section. */
  anyPermission?: PermissionName[]
  /** Required feature flag — section is hidden/redirected when off. */
  flag?: keyof FeatureFlags
  /**
   * With `flag` off the section stays visible to holders of this permission
   * only — for setup screens staff prepare before a feature goes live.
   */
  flagOffPermission?: PermissionName
  /** Second required flag (both must be on), mirroring nested API gates. */
  alsoFlag?: keyof FeatureFlags
  /** Inverse gate — section is hidden when this flag is ON. */
  hideWhen?: keyof FeatureFlags
  /** Reachable via ?section= but not a rail row nor a redirect target. */
  deepLinkOnly?: boolean
  /** Lazy component loader — wrapped in next/dynamic by the settings page. */
  load: () => Promise<ComponentType>
}

const REGISTRY = {
  // ── Clinic ──────────────────────────────────────────────────────────────
  general: {
    permission: "settings.general",
    load: () => import("@/components/settings/general-settings").then((m) => m.GeneralSettings),
  },
  "treatment-catalog": {
    permission: "treatment-catalog.manage",
    load: () => import("@/components/settings/treatment-catalog-settings").then((m) => m.TreatmentCatalogSettings),
  },
  // Rooms / Salla registry — shares the appointment-workflow write permission
  // with confirmation statuses by design.
  rooms: {
    permission: "settings.appointment_workflow",
    flag: "rooms_enabled",
    load: () => import("@/components/settings/rooms-settings").then((m) => m.RoomsSettings),
  },
  // Logistics driver registry — one permission covers the whole module's
  // writes (board assignments AND the roster).
  drivers: {
    permission: "logistics.manage",
    flag: "logistics_enabled",
    load: () => import("@/components/settings/drivers-settings").then((m) => m.DriversSettings),
  },
  // Albanian fiscalization — the tax figures the clinic maintains by hand.
  // Flag-gated, so the section only exists for clinics that fiscalize.
  fiscalization: {
    permission: "settings.general",
    flag: "fiscalization_enabled",
    load: () => import("@/components/settings/fiscalization-settings").then((m) => m.FiscalizationSettings),
  },
  // Clinic-wide default public-quote look (admin).
  "quote-design": {
    permission: "settings.general",
    flag: "quote_designs_enabled",
    load: () => import("@/components/settings/quote-design-settings").then((m) => m.QuoteDesignSettings),
  },

  // ── HR (one settings permission across all three catalogs) ─────────────
  "hr-holidays": {
    permission: "settings.hr",
    flag: "hr_core_enabled",
    load: () => import("@/components/settings/hr-holidays-settings").then((m) => m.HrHolidaysSettings),
  },
  "hr-shift-templates": {
    permission: "settings.hr",
    flag: "hr_roster_enabled",
    load: () => import("@/components/settings/hr-shift-templates-settings").then((m) => m.HrShiftTemplatesSettings),
  },
  "hr-leave-types": {
    permission: "settings.hr",
    flag: "hr_leave_enabled",
    load: () => import("@/components/settings/hr-leave-types-settings").then((m) => m.HrLeaveTypesSettings),
  },

  // ── Sales Pipeline ──────────────────────────────────────────────────────
  "clinic-statuses": {
    permission: "settings.lead_statuses",
    flag: "clinic_status_enabled",
    load: () => import("@/components/settings/clinic-statuses-settings").then(m => m.ClinicStatusesSettings),
  },
  "lead-statuses": {
    permission: "settings.lead_statuses",
    load: () => import("@/components/settings/lead-statuses-settings").then((m) => m.LeadStatusesSettings),
  },
  "recall-types": {
    permission: "settings.appointment_workflow",
    flag: "recalls_enabled",
    load: () => import("@/components/settings/recall-types-settings").then((m) => m.RecallTypesSettings),
  },
  // Simple clinic mode: small walk-in clinics don't track lead attribution.
  "lead-sources": {
    permission: "settings.lead_sources",
    hideWhen: "simple_clinic_mode",
    load: () => import("@/components/settings/lead-sources-settings").then((m) => m.LeadSourcesSettings),
  },
  // Multi-brand lead recycling — brand CRUD (admin + manager).
  brands: {
    permission: "brands.manage",
    flag: "lead_recycling_enabled",
    load: () => import("@/components/settings/brands-settings").then((m) => m.BrandsSettings),
  },
  // Hotel registry — the list a trip's accommodation block picks from. Adding
  // a hotel inline from the trip form does NOT need this grant; only the
  // settings CRUD does.
  hotels: {
    permission: "hotels.manage",
    flag: "hotels_enabled",
    load: () => import("@/components/settings/hotels-settings").then((m) => m.HotelsSettings),
  },
  targets: {
    permission: "targets.manage",
    hideWhen: "simple_clinic_mode",
    load: () => import("@/components/settings/targets-settings").then((m) => m.TargetsSettings),
  },
  questionnaires: {
    permission: "questionnaires.manage",
    load: () => import("@/components/settings/questionnaires-settings").then((m) => m.QuestionnairesSettings),
  },

  // ── Messaging ───────────────────────────────────────────────────────────
  "quick-reply-templates": {
    permission: "templates.manage",
    hideWhen: "simple_clinic_mode",
    load: () => import("@/components/settings/quick-reply-templates-settings").then((m) => m.QuickReplyTemplatesSettings),
  },
  "client-reminders": {
    permission: "settings.client_reminders",
    load: () => import("@/components/settings/client-reminders-settings").then((m) => m.ClientRemindersSettings),
  },
  // Staff notification center — which notification types (bells / nudges /
  // push) ring on this clinic. Catalog-driven: the toggle list comes from the
  // backend (StaffNotificationCatalog), labels from staffNotifications.* i18n.
  "staff-notifications": {
    permission: "settings.staff_notifications",
    load: () => import("@/components/settings/staff-notifications-settings").then((m) => m.StaffNotificationsSettings),
  },
  // The section component already returns null without this flag — gate the
  // rail row + deep link on it too, so a clinic with automation off never
  // lands on an empty settings page.
  "whatsapp-automation": {
    permission: "automation.manage",
    flag: "whatsapp_automation_enabled",
    load: () => import("@/components/settings/whatsapp-automation-settings").then((m) => m.WhatsAppAutomationSettings),
  },

  // ── Finance ─────────────────────────────────────────────────────────────
  // Only where the clinic runs Cash & banks. The money.* permissions are
  // granted fleet-wide by the migration, so a flag-off clinic would otherwise
  // show its admins and accountants a section they cannot use.
  "cash-banks": {
    anyPermission: ["money.accounts.view", "money.accounts.manage", "money.sessions.manage"],
    flag: "money_accounts_enabled",
    load: () => import("@/components/settings/cash-banks-settings").then((m) => m.CashBanksSettings),
  },
  // The API nests expense-category routes inside BOTH costs_enabled AND
  // expense_engine_enabled (404 when either is off) — mirror both gates.
  "expense-categories": {
    permission: "expense_categories.manage",
    flag: "expense_engine_enabled",
    alsoFlag: "costs_enabled",
    load: () => import("@/components/settings/expense-categories-settings").then((m) => m.ExpenseCategoriesSettings),
  },
  // Collections routes 404 without payments — mirror both gates.
  collections: {
    permission: "settings.general",
    flag: "collections_enabled",
    alsoFlag: "payments_enabled",
    load: () => import("@/components/settings/collections-settings").then((m) => m.CollectionsSettings),
  },
  // ArtDent 260924 — bank-account routes live inside payments_enabled. Shown
  // only with Cash & banks, so clinics without it see no new section (and,
  // with no accounts, no bank picker on their payment forms).
  "bank-accounts": {
    permission: "settings.general",
    flag: "payments_enabled",
    alsoFlag: "money_accounts_enabled",
    load: () => import("@/components/settings/bank-accounts-settings").then((m) => m.BankAccountsSettings),
  },

  // ── Integrations ────────────────────────────────────────────────────────
  whatsapp: {
    permission: "whatsapp.accounts.manage",
    load: () => import("@/components/settings/whatsapp-accounts-settings").then((m) => m.WhatsAppAccountsSettings),
  },
  "meta-leads": {
    permission: "meta-leads.manage",
    hideWhen: "simple_clinic_mode",
    load: () => import("@/components/settings/meta-leads-settings").then((m) => m.MetaLeadsSettings),
  },
  // Telephony kill switch (clinics with no phone system, e.g. albdental) —
  // hides the rail row AND the settings-page deep link in one entry.
  telephony: {
    permission: "calls.settings",
    flag: "telephony_enabled",
    load: () => import("@/components/settings/telephony-settings").then((m) => m.TelephonySettings),
  },
  // Call-centre outcome catalog (admin-only, same permission as telephony).
  // Deliberately NOT gated on telephony_enabled — Dentour's telephony flag may
  // be off while the catalog must still demo.
  "call-outcomes": {
    permission: "calls.settings",
    flag: "call_center_reporting_enabled",
    load: () => import("@/components/settings/call-outcomes-settings").then((m) => m.CallOutcomesSettings),
  },

  // ── System & Access ─────────────────────────────────────────────────────
  roles: {
    permission: "settings.roles",
    load: () => import("@/components/settings/roles-settings").then((m) => m.RolesSettings),
  },
  "feature-flags": {
    permission: "settings.general",
    hideWhen: "simple_clinic_mode",
    load: () => import("@/components/settings/feature-flags-settings").then((m) => m.FeatureFlagsSettings),
  },

  // ── Deep-link only ──────────────────────────────────────────────────────
  // Notification templates: NOT a rail row, but a reachable ?section= page —
  // gated here to keep the deep-link guard honest.
  templates: {
    permission: "settings.notification_templates",
    deepLinkOnly: true,
    load: () => import("@/components/settings/notification-templates-settings").then((m) => m.NotificationTemplatesSettings),
  },
} satisfies Record<string, SettingsSectionDef>

/** Canonical union of Settings sub-page ids (the `?section=` query value). */
export type SettingsSection = keyof typeof REGISTRY

/** The registry, widened so every entry exposes the full (optional) def shape. */
export const SETTINGS_SECTIONS: Record<SettingsSection, SettingsSectionDef> = REGISTRY

/**
 * Rail order of sections = registry declaration order minus deep-link-only
 * entries. Drives the rail rows and firstVisibleSection.
 */
export const SECTION_ORDER = (Object.keys(SETTINGS_SECTIONS) as SettingsSection[]).filter(
  (id) => !SETTINGS_SECTIONS[id].deepLinkOnly
)

/** Runtime guard for an arbitrary `?section=` string. */
export function isSettingsSection(value: string): value is SettingsSection {
  return Object.prototype.hasOwnProperty.call(REGISTRY, value)
}

/**
 * De-duplicated list of every permission that grants access to SOME settings
 * section — i.e. the set the page-level guard checks with `canAny(...)` to
 * decide whether a user may reach /settings at all.
 */
export const SETTINGS_PERMISSIONS = [
  ...new Set(
    (Object.keys(SETTINGS_SECTIONS) as SettingsSection[])
      .flatMap((id) => [
        SETTINGS_SECTIONS[id].permission,
        ...(SETTINGS_SECTIONS[id].anyPermission ?? []),
      ])
      .filter((p): p is PermissionName => p !== undefined)
  ),
]

/**
 * THE section gate — required/alternative permissions and feature flags, with hasFlag's
 * deny-on-missing default. Shared by the settings rail AND the settings page's
 * deep-link guard so a `?section=` URL can never reach a section the rail
 * would hide.
 */
export function sectionVisible(
  section: SettingsSection,
  can: (permission: string) => boolean,
  flags: FeatureFlags | undefined
): boolean {
  const def = SETTINGS_SECTIONS[section]
  if (def.permission || def.anyPermission) {
    const permitted =
      (def.permission ? can(def.permission) : false) ||
      (def.anyPermission?.some(can) ?? false)
    if (!permitted) return false
  }
  if (def.flag && !hasFlag(flags, def.flag)) {
    if (!def.flagOffPermission || !can(def.flagOffPermission)) return false
  }
  if (def.alsoFlag && !hasFlag(flags, def.alsoFlag)) return false
  if (def.hideWhen && hasFlag(flags, def.hideWhen)) return false
  return true
}

/**
 * First section (in rail order) the user is allowed to see. Sections with no
 * permission gate pass the permission test vacuously. Returns null if nothing
 * is visible (shouldn't happen for a user who passed the page-level `canAny`
 * guard).
 */
export function firstVisibleSection(
  can: (permission: string) => boolean,
  flags: FeatureFlags | undefined
): SettingsSection | null {
  for (const section of SECTION_ORDER) {
    if (sectionVisible(section, can, flags)) return section
  }
  return null
}
